import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ALL_TRAINING_BUNDLE_IDS, getTrainingBundleForCourse } from '@/config/trainingBundles';
import { isFullAccessSource } from '@/lib/access/enrollmentSources';
import { vorschauErlaubtLektion, vorschauEntziehtVollzugriff } from '@/lib/access/vorschauKurs';
import { useLocation } from 'react-router-dom';
import { darfSelbstErneuern, laeuftBaldAb } from '@/lib/sitzungAblauf';

interface TierSettings {
  webinar_recording_delay_days: number;
  kreativpaket_max_module: number;
  liederwerkstatt_monthly_limit: number;
}

interface EnrollmentInfo {
  courseId: string;
  source: string;
  courseCategory: string | null;
  courseSlug: string;
}

interface BundleCourseMapping {
  bundleId: string;
  courseId: string;
  courseCategory: string | null;
  courseSlug: string | null;
}

interface BundleWithTier {
  id: string;
  tierLevel: string | null;
}

interface AccessState {
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
  hasPartialFailure: boolean;
  userId: string | null;
  tier: 'start' | 'pro';
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** Course admins (assigned in AdminKurse) should be able to preview/access their own courses. */
  managedCourseIds: string[];
  settings: TierSettings | null;
  enrollments: EnrollmentInfo[];
  bundleEnrollments: string[]; // Bundle IDs the user is explicitly enrolled in
  effectiveBundleEnrollments: string[]; // Bundle IDs including tier-based access
  bundleCourses: BundleCourseMapping[]; // Which courses belong to which bundles
  plusMonthCourseId: string | null;
  challengeEnrollments: string[]; // Themenreise-IDs, in denen der User enrolled ist
  purchasedChallengeLessonIds: Set<string>; // Lesson-IDs, die über Themenreisen-Käufe freigeschaltet sind
  challengeTierRequirements: Map<string, string | null>; // challenge_id → tier_requirement (global)
  /** Saisonale Themenreisen mit Gratis-Lektionen für Starter (LIVE berechnet, nur tier==='start'). */
  starterFreeChallenges: Map<string, { freeLessonCount: number }>;
  /** Lesson-IDs, die für Starter über die aktive Saison-Themenreise gratis sind. */
  starterFreeChallengeLessonIds: Set<string>;
  /**
   * Kurse mit `preview_lessons_public`. Nur dort entscheidet `is_preview`
   * über den Lektionszugriff — siehe `@/lib/access/vorschauKurs`.
   */
  previewPublicCourseIds: Set<string>;




  // Helper functions
  hasFullAccessToCourse: (courseId: string) => boolean;
  hasAccessToChallenge: (challengeId: string) => boolean;
  isEnrolledInCourse: (courseId: string) => boolean;
  canAccessLesson: (params: {
    courseId: string;
    courseCategory: string | null;
    courseSlug: string;
    modulePosition: number;
    lessonId: string;
    isFirstLessonOfCourse: boolean;
    isFirstLessonOfModule: boolean;
    /** `course_lessons.is_preview` — wirkt nur in Kursen mit Vorschau-Schalter. */
    isPreview?: boolean;
  }) => boolean;
  isLessonLocked: (params: {
    courseId: string;
    courseCategory: string | null;
    courseSlug: string;
    modulePosition: number;
    lessonId: string;
    isFirstLessonOfCourse: boolean;
    isFirstLessonOfModule: boolean;
    /** `course_lessons.is_preview` — wirkt nur in Kursen mit Vorschau-Schalter. */
    isPreview?: boolean;
  }) => boolean;

  // Refresh function (call after purchase, etc.)
  refresh: () => void;
}

const normalizeTier = (raw?: string | null): 'start' | 'pro' => {
  const t = (raw || '').toLowerCase();
  if (t === 'pro') return 'pro';
  // Map legacy 'plus' and 'basic' to 'pro' (Mitgliedschaft)
  if (t === 'plus' || t === 'basic') return 'pro';
  // legacy 'free' (and unknown values) should behave like 'start'
  return 'start';
};

const AccessContext = createContext<AccessState | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [userId, setUserId] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Only load heavy access data on routes that actually need it.
  // This keeps the public homepage and marketing pages fast for logged-in users.
  const isAccessRelevantRoute = useMemo(() => {
    const path = location.pathname || "/";
    return (
      path.startsWith('/mein-bereich') ||
      path.startsWith('/admin') ||
      path.startsWith('/checkout') ||
      path.startsWith('/abo-checkout') ||
      path.startsWith('/termin-checkout') ||
      path.startsWith('/kurs-vorschau') ||
      path.startsWith('/themenreise') ||
      path.startsWith('/themenreisen') ||
      path.startsWith('/themenreise-erfolgreich')
    );
  }, [location.pathname]);

  const isAccessRelevantRouteRef = useRef(isAccessRelevantRoute);
  useEffect(() => {
    isAccessRelevantRouteRef.current = isAccessRelevantRoute;
  }, [isAccessRelevantRoute]);

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session - validate JWT is not expired before trusting it.
    // Token lesen nur über `sitzungAblauf.ts` (base64url): `atob()` scheiterte bei Namen wie „Großmann“ und löste
    // ein unnötiges Erneuern aus (03.11.2026). Unbekannter Ablauf heißt: Supabase erneuert selbst.
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session && laeuftBaldAb(session) === true && darfSelbstErneuern()) {
        // Expired - refresh it
        console.log('[AccessContext] Initial session expired, refreshing...');
        const { data: refreshData } = await supabase.auth.refreshSession();
        setUserId(refreshData?.session?.user?.id || null);
        setIsAuthReady(true);
        return;
      }
      
      setUserId(session?.user?.id || null);
      setIsAuthReady(true);
    };
    
    initAuth();

    const queueAccessInvalidation = () => {
      window.setTimeout(() => {
        if (!isAccessRelevantRouteRef.current) return;
        queryClient.invalidateQueries({ queryKey: ['global-access'] });
      }, 0);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id || null;

      // Ensure we always refetch access after auth events (re-login, token refresh),
      // even when the userId stays the same (React Query cache would otherwise stay "fresh").
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'SIGNED_OUT') {
        queueAccessInvalidation();
      }

      setUserId(prevUserId => {
        if (newUserId !== prevUserId) {
          // Invalidate all access queries when user changes
          queueAccessInvalidation();
        }
        return newUserId;
      });
      setIsAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Single query that fetches ALL access-related data at once
  const { data: accessData, isLoading, isError, refetch } = useQuery({
    queryKey: ['global-access', userId],
    queryFn: async () => {
      // Themenreisen-Tier-Requirements: global, nicht user-spezifisch.
      // Wird auch für nicht eingeloggte User geladen.
      const { data: challengeTierData } = await supabase
        .from('learning_challenges')
        .select('id, tier_requirement');
      const challengeTierRequirements = new Map<string, string | null>(
        (challengeTierData || []).map((c: any) => [c.id as string, (c.tier_requirement as string | null) ?? null])
      );

      if (!userId) {
        return {
          tier: 'start' as const,
          isAdmin: false,
          isSuperAdmin: false,
          managedCourseIds: [],
          settings: null,
          enrollments: [],
          bundleEnrollments: [],
          allBundles: [],
          bundleCourses: [],
          plusMonthCourseId: null,
          challengeEnrollments: [] as string[],
          purchasedChallengeLessonIds: new Set<string>(),
          challengeTierRequirements,
          starterFreeChallenges: new Map<string, { freeLessonCount: number }>(),
          starterFreeChallengeLessonIds: new Set<string>(),
          previewPublicCourseIds: new Set<string>(),
          hasPartialFailure: false,
        };
      }



      // Fetch all data in parallel using Promise.allSettled for resilience (M-6 fix)
      const results = await Promise.allSettled([
        // 0. User tier from user_usage
        supabase
          .from('user_usage')
          .select('subscription_tier')
          .eq('user_id', userId)
          .maybeSingle(),

        // 1. Admin role check (super_admin, admin, course_admin)
        Promise.all([
          supabase.rpc('has_role', { _user_id: userId, _role: 'super_admin' }),
          supabase.rpc('has_role', { _user_id: userId, _role: 'admin' }),
          supabase.rpc('has_role', { _user_id: userId, _role: 'course_admin' }),
        ]),

        // 2. Course admin assignments
        supabase
          .from('course_admin_assignments')
          .select('course_id')
          .eq('user_id', userId),

        // 3. All course enrollments with course info
        supabase
          .from('course_enrollments')
          .select(`
            course_id,
            source,
            courses!inner (
              id,
              category,
              slug
            )
          `)
          .eq('user_id', userId),

        // 4. Bundle enrollments (explicit) – nur Enrollments deren Startdatum erreicht ist
        supabase
          .from('bundle_enrollments')
          .select('bundle_id')
          .eq('user_id', userId)
          .lte('enrolled_at', new Date().toISOString()),

        // 5. All bundles with tier_level
        supabase
          .from('bundles')
          .select('id, tier_level'),

        // 6. All bundle-course mappings
        supabase
          .from('bundle_courses')
          .select('bundle_id, course_id, courses(category, slug)'),

        // 7. Current monthly course (Kurs des Monats)
        supabase
          .from('plus_course_schedule')
          .select('course_id')
          .eq('is_released', true)
          .order('released_at', { ascending: false })
          .limit(1)
          .maybeSingle(),

        // 8. Themenreisen-Enrollments des Users (RLS: nur eigene Rows)
        supabase
          .from('challenge_enrollments')
          .select('challenge_id')
          .eq('user_id', userId),
      ]);


      // Helper: erkennt fehlgeschlagene Settled-Results.
      // Supabase rejected fast nie; "Fehler" heißt: rejected ODER value.error gesetzt.
      // Für results[1] (Promise.all aus 3 RPCs) sind alle drei { data, error } zu prüfen.
      const isFailed = (r: PromiseSettledResult<any>): boolean => {
        if (r.status === 'rejected') return true;
        const v = (r as any).value;
        if (Array.isArray(v)) return v.some((x: any) => x?.error);
        return Boolean(v?.error);
      };

      // KRITISCH: tier (results[0]) und Admin-Rollen (results[1]) dürfen niemals
      // still als leerer Erfolg gecached werden → werfen, damit react-query retried
      // und bei dauerhaftem Fehlschlag die letzten guten Daten erhalten bleiben.
      if (isFailed(results[0]) || isFailed(results[1])) {
        throw new Error("AccessContext: Kritische Zugriffsdaten konnten nicht geladen werden");
      }

      // Unkritische Queries (results[2]..[8]) bleiben mit Fallbacks; Teilausfall wird geflaggt.
      const hasPartialFailure = results.slice(2, 9).some(isFailed);

      // Extract results with safe fallbacks for failed queries
      const getValue = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
        result.status === 'fulfilled' ? result.value : fallback;

      const userUsageResult = getValue(results[0] as any, { data: null });
      const adminRoleResult = getValue(results[1] as any, [{ data: false }, { data: false }, { data: false }]);
      const courseAdminAssignmentsResult = getValue(results[2] as any, { data: [] });
      const enrollmentsResult = getValue(results[3] as any, { data: [] });
      const bundleEnrollmentsResult = getValue(results[4] as any, { data: [] });
      const allBundlesResult = getValue(results[5] as any, { data: [] });
      const bundleCoursesResult = getValue(results[6] as any, { data: [] });
      const plusCourseResult = getValue(results[7] as any, { data: null });
      const challengeEnrollmentsResult = getValue(results[8] as any, { data: [] });

      const tier = normalizeTier(userUsageResult.data?.subscription_tier);
      const isSuperAdmin = adminRoleResult[0].data === true; // super_admin (index 0)
      const isFullAdmin = adminRoleResult[1].data === true; // admin (index 1)
      const isCourseAdmin = adminRoleResult[2].data === true; // course_admin (index 2)
      // isAdmin = true for ANY admin role (shows admin button, grants admin access)
      const isAdmin = isSuperAdmin || isFullAdmin || isCourseAdmin;
      
      // 7. Fetch tier settings (depends on tier)
      const { data: tierSettingsData } = await supabase
        .from('tier_settings')
        .select('webinar_recording_delay_days, kreativpaket_max_module, liederwerkstatt_monthly_limit')
        .eq('tier', tier)
        .maybeSingle();

      // Kurse mit kursweiter Vorschau-Sperre. Bewusst als eigene Abfrage statt im
      // Promise-Bündel oben: das ist index-basiert (results[0]..[8]) und würde
      // beim Einfügen leicht verrutschen. Die Liste ist klein (heute ein Kurs).
      const previewPublicCourseIds = new Set<string>();
      {
        const { data: vorschauKurse } = await supabase
          .from('courses')
          .select('id')
          .eq('preview_lessons_public', true);
        for (const row of (vorschauKurse || []) as Array<{ id: string }>) {
          if (row.id) previewPublicCourseIds.add(row.id);
        }
      }

      // Fallback settings wenn DB-Werte fehlen (sollte nach RLS-Fix nicht mehr passieren)
      const settings: TierSettings = {
        webinar_recording_delay_days: tierSettingsData?.webinar_recording_delay_days ?? 30,
        kreativpaket_max_module: tierSettingsData?.kreativpaket_max_module ?? 0, // Striktester Fallback
        liederwerkstatt_monthly_limit: tierSettingsData?.liederwerkstatt_monthly_limit ?? 1,
      };

      // Transform enrollments
      const enrollments: EnrollmentInfo[] = (enrollmentsResult.data || []).map((e: any) => ({
        courseId: e.course_id,
        source: e.source,
        courseCategory: e.courses?.category || null,
        courseSlug: e.courses?.slug || '',
      }));

      // Courses this user can administer (preview access)
      const managedCourseIds: string[] = (courseAdminAssignmentsResult.data || []).map((a: any) => a.course_id);
      
      // Bundle enrollments (IDs of bundles the user is explicitly enrolled in)
      const bundleEnrollments: string[] = (bundleEnrollmentsResult.data || []).map((e: any) => e.bundle_id);
      
      // All bundles with tier_level
      const allBundles: BundleWithTier[] = (allBundlesResult.data || []).map((b: any) => ({
        id: b.id,
        tierLevel: b.tier_level,
      }));
      
      // Bundle-course mappings with course info for Kreativpaket detection
      const bundleCourses: BundleCourseMapping[] = (bundleCoursesResult.data || []).map((e: any) => ({
        bundleId: e.bundle_id,
        courseId: e.course_id,
        courseCategory: e.courses?.category || null,
        courseSlug: e.courses?.slug || null,
      }));

      // Themenreisen-Enrollments des Users → freigeschaltete Lektionen ermitteln
      const challengeEnrollments: string[] = (challengeEnrollmentsResult.data || [])
        .map((e: any) => e.challenge_id as string);

      const purchasedChallengeLessonIds = new Set<string>();
      if (challengeEnrollments.length > 0) {
        const { data: challengeLessonRows } = await supabase
          .from('challenge_lessons')
          .select('lesson_id')
          .in('challenge_id', challengeEnrollments);
        for (const row of (challengeLessonRows || []) as Array<{ lesson_id: string }>) {
          if (row.lesson_id) purchasedChallengeLessonIds.add(row.lesson_id);
        }
      }

      // ---- Starter-Gratiszugriff für saisonale Themenreisen (rein additiv, nur tier==='start') ----
      const starterFreeChallenges = new Map<string, { freeLessonCount: number }>();
      const starterFreeChallengeLessonIds = new Set<string>();
      if (tier === 'start') {
        const { data: starterChallengeRows } = await supabase
          .from('learning_challenges')
          .select('id, slug, is_seasonal, season_start, season_end, starter_free_lesson_count')
          .eq('is_active', true)
          .gt('starter_free_lesson_count', 0);
        const nowMs = Date.now();
        const qualifying = (starterChallengeRows || []).filter((c: any) => {
          if (!c.is_seasonal) return false;
          if (!c.season_start || !c.season_end) return false;
          const start = new Date(c.season_start).getTime();
          const end = new Date(c.season_end).getTime();
          if (Number.isNaN(start) || Number.isNaN(end)) return false;
          return nowMs >= start && nowMs <= end;
        });
        for (const c of qualifying) {
          const n = Number(c.starter_free_lesson_count) || 0;
          if (n <= 0) continue;
          starterFreeChallenges.set(c.id as string, { freeLessonCount: n });
          const { data: lessonRows } = await supabase
            .from('challenge_lessons')
            .select('lesson_id, sort_order')
            .eq('challenge_id', c.id)
            .order('sort_order', { ascending: true })
            .limit(n);
          for (const row of (lessonRows || []) as Array<{ lesson_id: string }>) {
            if (row.lesson_id) starterFreeChallengeLessonIds.add(row.lesson_id);
          }
        }
      }

      return {
        tier,
        isAdmin,
        isSuperAdmin,
        managedCourseIds,
        settings,
        enrollments,
        bundleEnrollments,
        allBundles,
        bundleCourses,
        plusMonthCourseId: plusCourseResult.data?.course_id || null,
        challengeEnrollments,
        purchasedChallengeLessonIds,
        challengeTierRequirements,
        starterFreeChallenges,
        starterFreeChallengeLessonIds,
        previewPublicCourseIds,
        hasPartialFailure,
      };

    },

    enabled: isAuthReady && isAccessRelevantRoute, // Only start query once auth is known AND the route needs it
    staleTime: 5 * 60 * 1000, // 5 minutes - then auto-refetch on next access
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  // Calculate effective bundle enrollments (explicit + tier-based)
  const effectiveBundleEnrollments = useMemo((): string[] => {
    if (!accessData) return [];
    
    const explicit = accessData.bundleEnrollments;
    const tier = accessData.tier;
    const allBundles = accessData.allBundles || [];
    
    // Get tier-based bundle IDs
    const tierBasedBundleIds = allBundles
      .filter(bundle => {
        const tierLevel = bundle.tierLevel?.toLowerCase();
        // Bundles with tier_level 'none' or null are never automatically granted
        if (!tierLevel || tierLevel === 'none') return false;
        
        // Pro users (including migrated Plus users) get all bundles with any tier_level
        if (tier === 'pro') return true;
        // Start users get only 'start' bundles
        if (tier === 'start') return tierLevel === 'start';
        
        return false;
      })
      .map(b => b.id);
    
    // Merge explicit and tier-based, removing duplicates
    return [...new Set([...explicit, ...tierBasedBundleIds])];
  }, [accessData]);

  // Helper: Check if course is in any of user's enrolled bundles (including tier-based)
  const isCourseInEnrolledBundle = useCallback((courseId: string): boolean => {
    if (!accessData) return false;
    
    // Find all bundles that contain this course
    const bundlesContainingCourse = accessData.bundleCourses
      .filter(bc => bc.courseId === courseId)
      .map(bc => bc.bundleId);
    
    // Check if user has access to any of these bundles (explicit or tier-based)
    return bundlesContainingCourse.some(bundleId => 
      effectiveBundleEnrollments.includes(bundleId)
    );
  }, [accessData, effectiveBundleEnrollments]);

  // Helper: Check if a course belongs to a training bundle (tier_level: 'none')
  const isInTrainingBundle = useCallback((courseId: string): boolean => {
    if (!accessData) return false;
    return accessData.bundleCourses.some(
      bc => bc.courseId === courseId && ALL_TRAINING_BUNDLE_IDS.includes(bc.bundleId as any)
    );
  }, [accessData]);

  // Helper: Check if user has full access to a course (purchase/bundle enrollment/admin)
  // KREATIVPAKET SPECIAL RULE: Bundle access does NOT grant full Kreativpaket access!
  // TRAINING BUNDLE RULE: Training courses (tier_level: 'none') require explicit enrollment!
  const hasFullAccessToCourse = useCallback((courseId: string): boolean => {
    if (!accessData) return false;
    if (accessData.isAdmin) return true;
    // Course admins should be able to preview/access their assigned courses
    if (accessData.managedCourseIds?.includes(courseId)) return true;

    // TRAINING BUNDLE CHECK - must come BEFORE Pro tier check!
    // Training bundles (tier_level: 'none') require explicit enrollment
    if (isInTrainingBundle(courseId)) {
      // Check direct enrollment with full-access source
      const enrollment = accessData.enrollments.find(e => e.courseId === courseId);
      if (enrollment && isFullAccessSource(enrollment.source)) {
        return true;
      }
      // Check explicit bundle enrollment
      const trainingBundleId = getTrainingBundleForCourse(courseId, accessData.bundleCourses);
      if (trainingBundleId && accessData.bundleEnrollments.includes(trainingBundleId)) {
        return true;
      }
      return false; // No access to training courses without explicit enrollment
    }

    // Kursweite Vorschau-Sperre: In diesem Kurs darf ein Nicht-Mitglied die
    // Vollzugriff-Abkuerzung nicht bekommen. Sonst wuerde eine kostenlose
    // Einschreibung (Source 'free' = Full-Access-Source) die Lektionspruefung
    // ueberspringen und auch das Material freigeben.
    if (vorschauEntziehtVollzugriff({
      kursHatVorschauSchalter: accessData.previewPublicCourseIds.has(courseId),
      tier: accessData.tier,
      isAdmin: accessData.isAdmin,
      istVerwalteterKurs: accessData.managedCourseIds?.includes(courseId) ?? false,
    })) {
      return false;
    }

    // Pro users get full access to all NON-training courses
    if (accessData.tier === 'pro') return true;

    // Find enrollment info (may contain category/slug)
    const enrollment = accessData.enrollments.find(e => e.courseId === courseId);

    // Check direct enrollment with purchase, admin, bundle, or free source FIRST
    // This grants full access regardless of course type
    // "bundle" source = explicit bundle purchase → full access
    // "tier_access" source = tier-based bundle access → subject to tier restrictions (NOT full access)
    // "free" source = explicit manual/flow enrollment into a free course → full access
    if (enrollment && isFullAccessSource(enrollment.source)) {
      return true;
    }

    // Determine if this is a Kreativpaket (check enrollment info OR bundle_courses)
    let isKreativpaket = false;
    if (enrollment) {
      const category = enrollment.courseCategory?.toLowerCase() || '';
      const slug = enrollment.courseSlug?.toLowerCase() || '';
      isKreativpaket = category === 'kreativpakete' || slug.includes('kreativpaket');
    } else {
      // No direct enrollment - check bundle_courses for category/slug info
      // This is critical for Plus users who access courses via tier-based bundles without direct enrollments
      const bundleCourse = accessData.bundleCourses.find(bc => bc.courseId === courseId);
      if (bundleCourse) {
        const category = bundleCourse.courseCategory?.toLowerCase() || '';
        const slug = bundleCourse.courseSlug?.toLowerCase() || '';
        isKreativpaket = category === 'kreativpakete' || slug.includes('kreativpaket');
      }
    }

    // KREATIVPAKET SPECIAL RULE: Bundle access does NOT grant full Kreativpaket access!
    // For Kreativpakete, only purchase/admin (checked above) grants full access.
    // Plus/Start users with bundle access get limited module access via canAccessLesson.
    if (isKreativpaket) {
      return false; // Not full access - let canAccessLesson handle module restrictions
    }

    // For non-Kreativpaket courses: Bundle access grants full access
    if (isCourseInEnrolledBundle(courseId)) return true;

    // No full access
    return false;
  }, [accessData, isCourseInEnrolledBundle]);

  // Helper: Check if user can access a specific lesson
  const canAccessLesson = useCallback((params: {
    courseId: string;
    courseCategory: string | null;
    courseSlug: string;
    modulePosition: number;
    lessonId: string;
    isFirstLessonOfCourse: boolean;
    isFirstLessonOfModule: boolean;
    isPreview?: boolean;
  }): boolean => {
    if (!accessData) return false;

    // Additiv: Starter darf Gratis-Lektionen einer aktiven Saison-Themenreise sehen.
    // Greift NUR für tier==='start' und nur für vorab gesammelte Lesson-IDs.
    if (accessData.tier === 'start' && accessData.starterFreeChallengeLessonIds.has(params.lessonId)) {
      return true;
    }


    const { courseId, courseCategory, courseSlug, modulePosition, isFirstLessonOfCourse, isFirstLessonOfModule } = params;

    // 1. Admins always have full access
    if (accessData.isAdmin) return true;

    // 1b. Course admins always have full access to THEIR assigned courses (preview/edit workflow)
    if (accessData.managedCourseIds?.includes(courseId)) return true;

    // 2. TRAINING BUNDLE CHECK - must come BEFORE Pro tier check!
    // Training bundles (tier_level: 'none') require explicit enrollment
    if (isInTrainingBundle(courseId)) {
      // Check direct enrollment with full-access source
      const enrollment = accessData.enrollments.find(e => e.courseId === courseId);
      if (enrollment && isFullAccessSource(enrollment.source)) {
        return true;
      }
      // Check explicit bundle enrollment
      const trainingBundleId = getTrainingBundleForCourse(courseId, accessData.bundleCourses);
      if (trainingBundleId && accessData.bundleEnrollments.includes(trainingBundleId)) {
        return true;
      }
      return false; // No access to training courses without explicit enrollment
    }

    // 2b. Kursweite Vorschau-Sperre.
    // Greift nur in Kursen mit `preview_lessons_public` und nur fuer
    // eingeschriebene Nicht-Mitglieder. Muss VOR der Einschreibungs-Pruefung
    // stehen, weil eine Full-Access-Source sonst alles freigaebe.
    // Mitglieder, Admins und nicht Eingeschriebene fallen durch (null).
    const vorschau = vorschauErlaubtLektion(
      {
        kursHatVorschauSchalter: accessData.previewPublicCourseIds.has(courseId),
        tier: accessData.tier,
        isAdmin: accessData.isAdmin,
        istVerwalteterKurs: accessData.managedCourseIds?.includes(courseId) ?? false,
        istEingeschrieben:
          accessData.enrollments.some(e => e.courseId === courseId) ||
          isCourseInEnrolledBundle(courseId),
      },
      params.isPreview === true,
    );
    if (vorschau !== null) return vorschau;

    // 3. Pro users have full access to all NON-training courses
    if (accessData.tier === 'pro') return true;

    // 3b. Themenreisen-Kauf-Pfad: hat der User eine Themenreise gekauft,
    // die diese Lektion enthält? Dann Zugriff auf genau diese Lektion.
    if (accessData.purchasedChallengeLessonIds.has(params.lessonId)) {
      return true;
    }

    // 3. Check direct enrollment FIRST - purchase/admin/bundle/free grants full access
    // "bundle" source = explicit bundle purchase → full access
    // "tier_access" source = tier-based bundle access → subject to tier restrictions (NOT here)
    // "free" source = explicit manual/flow enrollment → full access
    const enrollment = accessData.enrollments.find(e => e.courseId === courseId);
    if (enrollment && isFullAccessSource(enrollment.source)) {
      return true;
    }

    // Determine course type for special rules
    const category = courseCategory?.toLowerCase() || '';
    const isKreativpaket = category === 'kreativpakete' || courseSlug.toLowerCase().includes('kreativpaket');
    const isJournalartikel = category === 'journalartikel';

    // 4. KREATIVPAKET SPECIAL RULE - applies BEFORE bundle check!
    // Only explicit purchase/admin enrollment (checked above) grants full Kreativpaket access.
    // Tier-based bundle access does NOT grant full Kreativpaket access.
    if (isKreativpaket) {
      // maxModule = sort_order des höchsten erlaubten Moduls (0 = nur erstes Modul)
      const maxModule = accessData.settings?.kreativpaket_max_module ?? 0;

      // Start: nur erstes Modul (sort_order 0), nur erste Lektion
      if (accessData.tier === 'start') {
        if (modulePosition > 0) return false;
        return isFirstLessonOfCourse;
      }
      // Pro: Access bis einschließlich maxModule (usually 999 for full access)
      return modulePosition <= maxModule;
    }

    // 5. Bundle access check (for non-Kreativpaket courses)
    if (isCourseInEnrolledBundle(courseId)) return true;

    // 6. START users: only first lesson of entire course
    if (accessData.tier === 'start') {
      return isFirstLessonOfCourse;
    }

    // 7. PRO users: full access (Journalartikel, all courses)
    if (accessData.tier === 'pro') {
      return true;
    }

    // 8. Default: no access
    return false;
  }, [accessData, isCourseInEnrolledBundle]);

  // Helper: Check if a lesson should show as locked
  // This now simply uses canAccessLesson - the logic is centralized there
  const isLessonLocked = useCallback((params: {
    courseId: string;
    courseCategory: string | null;
    courseSlug: string;
    modulePosition: number;
    lessonId: string;
    isFirstLessonOfCourse: boolean;
    isFirstLessonOfModule: boolean;
    isPreview?: boolean;
  }): boolean => {
    return !canAccessLesson(params);
  }, [canAccessLesson]);

  // Helper: Check if user is enrolled in a course (any source) for search/display purposes
  // This is DIFFERENT from hasFullAccessToCourse which checks for unrestricted module access
  const isEnrolledInCourse = useCallback((courseId: string): boolean => {
    if (!accessData) return false;
    if (accessData.isAdmin) return true;
    if (accessData.tier === 'pro') return true;
    
    // Check direct enrollment (any source including tier_access)
    if (accessData.enrollments.some(e => e.courseId === courseId)) {
      return true;
    }
    
    // Check bundle access (explicit + tier-based)
    return isCourseInEnrolledBundle(courseId);
  }, [accessData, isCourseInEnrolledBundle]);

  // Helper: Hat der User Zugriff auf eine Themenreise?
  const hasAccessToChallenge = useCallback((challengeId: string): boolean => {
    if (!accessData) return false;
    if (accessData.isAdmin || accessData.isSuperAdmin) return true;
    // Pro deckt alle Themenreisen ab (heutige tier_requirements sind 'start'/'pro'/null).
    if (accessData.tier === 'pro') return true;
    if (accessData.challengeEnrollments.includes(challengeId)) return true;
    return false;
  }, [accessData]);

  // Refresh function
  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const value: AccessState = useMemo(() => ({
    isLoading: !isAuthReady || isLoading, // Include auth initialization in loading state
    isReady: isAuthReady && !isLoading && !!accessData,
    isError,
    hasPartialFailure: accessData?.hasPartialFailure ?? false,
    userId,
    tier: accessData?.tier || 'start',
    isAdmin: accessData?.isAdmin || false,
    isSuperAdmin: accessData?.isSuperAdmin || false,
    managedCourseIds: accessData?.managedCourseIds || [],
    settings: accessData?.settings || null,
    enrollments: accessData?.enrollments || [],
    bundleEnrollments: accessData?.bundleEnrollments || [],
    effectiveBundleEnrollments,
    bundleCourses: accessData?.bundleCourses || [],
    plusMonthCourseId: accessData?.plusMonthCourseId || null,
    challengeEnrollments: accessData?.challengeEnrollments || [],
    purchasedChallengeLessonIds: accessData?.purchasedChallengeLessonIds || new Set<string>(),
    challengeTierRequirements: accessData?.challengeTierRequirements || new Map<string, string | null>(),
    starterFreeChallenges: accessData?.starterFreeChallenges || new Map<string, { freeLessonCount: number }>(),
    starterFreeChallengeLessonIds: accessData?.starterFreeChallengeLessonIds || new Set<string>(),
    previewPublicCourseIds: accessData?.previewPublicCourseIds || new Set<string>(),

    hasFullAccessToCourse,
    hasAccessToChallenge,
    isEnrolledInCourse,
    canAccessLesson,
    isLessonLocked,
    refresh,
  }), [isAuthReady, isLoading, isError, accessData, userId, effectiveBundleEnrollments, hasFullAccessToCourse, hasAccessToChallenge, isEnrolledInCourse, canAccessLesson, isLessonLocked, refresh]);

  return (
    <AccessContext.Provider value={value}>
      {children}
    </AccessContext.Provider>
  );
}

export function useAccess() {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within an AccessProvider');
  }
  return context;
}

// Optional hook that returns null if no provider
export function useOptionalAccess() {
  return useContext(AccessContext);
}
